<template>
  <section id="consulenza" class="final-cta" :style="{ backgroundImage: 'url(/images/sport-equipment.webp)' }" aria-labelledby="final-cta-title">
    <div class="final-cta-inner">
      <div class="final-cta-content">
        <h2 id="final-cta-title" class="final-cta-title">Portiamo metodo e struttura nel tuo contesto sportivo</h2>
        <p class="final-cta-text">
          Ogni società sportiva ha esigenze specifiche, risorse differenti e obiettivi propri. Il primo passo è comprenderli con attenzione. Da lì possiamo definire un percorso coerente, sostenibile e adattato alla tua realtà.
        </p>
      </div>

      <div class="final-cta-form-wrapper">
        <form
          class="final-cta-form"
          @submit.prevent="handleSubmit"
          novalidate
        >
          <div class="form-row">
            <div class="form-group">
              <label for="nome">Nome <span class="required" aria-hidden="true">*</span></label>
              <input
                id="nome"
                v-model="nome"
                name="nome"
                type="text"
                required
                autocomplete="given-name"
              />
            </div>
            <div class="form-group">
              <label for="cognome">Cognome <span class="required" aria-hidden="true">*</span></label>
              <input
                id="cognome"
                v-model="cognome"
                name="cognome"
                type="text"
                required
                autocomplete="family-name"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">E-mail <span class="required" aria-hidden="true">*</span></label>
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                required
                autocomplete="email"
                @blur="emailTouched = true"
              />
              <span v-if="emailError" class="field-error" role="alert">{{ emailError }}</span>
            </div>
            <div class="form-group">
              <label for="telefono">Telefono</label>
              <input
                id="telefono"
                v-model="telefono"
                name="telefono"
                type="tel"
                autocomplete="tel"
              />
              <span v-if="phoneError" class="field-error" role="alert">{{ phoneError }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="messaggio">Messaggio <span class="required" aria-hidden="true">*</span></label>
            <textarea
              id="messaggio"
              v-model="messaggio"
              name="messaggio"
              required
              rows="6"
            ></textarea>
          </div>

          <p class="form-required-note">* Campi obbligatori</p>

          <div class="form-group form-group--checkbox">
            <label class="checkbox-label">
              <input
                id="privacy"
                v-model="privacy"
                type="checkbox"
                required
                @change="privacyTouched = true"
              />
              <span>
                Ho letto e accetto la
                <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </span>
            </label>
            <span v-if="!privacy && privacyTouched" class="field-error" role="alert">
              Devi accettare la Privacy Policy per inviare il messaggio.
            </span>
          </div>

          <div v-if="status === 'success'" class="form-success" role="alert">
            <p>Grazie. Abbiamo ricevuto la tua richiesta e ti risponderemo al più presto.</p>
          </div>
          <div v-else-if="status === 'error'" class="form-error" role="alert">
            <p>Si è verificato un errore nell'invio. Riprova o scrivici direttamente a <a href="mailto:info@sportemocracy.it">info@sportemocracy.it</a>.</p>
          </div>

          <button
            type="submit"
            class="cta-submit"
            :disabled="!isFormValid || status === 'loading'"
          >{{ status === 'loading' ? 'Invio in corso…' : 'Invia' }}</button>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^(\+39[\s]?)?((0[0-9]{1,4}[\s\-]?[0-9]{4,8})|(3[0-9]{8,9}))$/;

const nome = ref('');
const cognome = ref('');
const email = ref('');
const emailTouched = ref(false);
const telefono = ref('');
const messaggio = ref('');
const privacy = ref(false);
const privacyTouched = ref(false);
const status = ref('idle'); // idle | loading | success | error

const emailError = computed(() => {
  const val = email.value.trim();
  if (val === '' || !emailTouched.value) return null;
  return EMAIL_PATTERN.test(val) ? null : 'Inserisci un indirizzo e-mail valido.';
});

const phoneError = computed(() => {
  const tel = telefono.value.trim();
  if (tel === '') return null;
  return PHONE_PATTERN.test(tel) ? null : 'Inserisci un numero di telefono italiano valido.';
});

const isFormValid = computed(() =>
  nome.value.trim() !== '' &&
  cognome.value.trim() !== '' &&
  EMAIL_PATTERN.test(email.value.trim()) &&
  phoneError.value === null &&
  messaggio.value.trim() !== '' &&
  privacy.value === true
);

async function handleSubmit() {
  if (!isFormValid.value || status.value === 'loading') return;
  status.value = 'loading';
  try {
    const response = await fetch('/.netlify/functions/send-contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nome: nome.value,
        cognome: cognome.value,
        email: email.value,
        telefono: telefono.value,
        messaggio: messaggio.value
      })
    });
    const data = await response.json();
    if (data.success) {
      status.value = 'success';
      nome.value = '';
      cognome.value = '';
      email.value = '';
      telefono.value = '';
      messaggio.value = '';
      privacy.value = false;
      privacyTouched.value = false;
    } else {
      status.value = 'error';
    }
  } catch {
    status.value = 'error';
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

.final-cta {
  position: relative;
  background-size: cover;
  background-position: center;
  color: $white;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.35);
    z-index: 0;
  }
}

.final-cta-inner {
  position: relative;
  z-index: 1;
  padding: 56px 24px;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* ── Contenuto testuale ── */

.final-cta-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.final-cta-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
}

.final-cta-text {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba($white, 0.88);
}

/* ── Form ── */

.final-cta-form-wrapper {
  width: 100%;
}

.final-cta-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  label {
    font-size: 0.9rem;
    font-weight: 700;
    color: $white;
    letter-spacing: 0.02em;
  }

  input,
  textarea {
    width: 100%;
    padding: 12px 16px;
    background-color: $white;
    border: 1px solid rgba($white, 0.6);
    border-radius: 6px;
    color: $black;
    font-size: 1rem;
    font-family: inherit;
    line-height: 1.5;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &::placeholder {
      color: rgba($black, 0.35);
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0px 1000px $white inset;
      -webkit-text-fill-color: $black;
    }

    &:focus {
      outline: none;
      border-color: $white;
    }

    &:focus-visible {
      outline: 2px solid $white;
      outline-offset: 2px;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
}

.required {
  color: rgba($white, 0.75);
  font-weight: 400;
}

.field-error {
  font-size: 0.82rem;
  color: $white;
}

.form-group--checkbox {
  gap: 8px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba($white, 0.88);

  input[type="checkbox"] {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: $white;
    cursor: pointer;
  }

  a {
    color: $white;
    text-decoration: underline;

    &:hover {
      color: rgba($white, 0.8);
    }
  }
}

.form-required-note {
  margin: 0;
  font-size: 0.82rem;
  color: $white;
}

.cta-submit {
  position: relative;
  align-self: flex-start;
  width: 100%;
  padding: 16px 32px;
  background-color: $primary;
  color: $white;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 39px;
  border: 1px solid $white;
  white-space: nowrap;

  &:hover {
    background-color: $primary-light;
    color: $black;
  }

  &:focus-visible {
    outline: 2px solid $white;
    outline-offset: 3px;
  }
}

/* ── Feedback invio ── */

.form-success,
.form-error {
  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
  }
}

.form-success p {
  color: rgba($white, 0.88);
}

.form-error p {
  color: rgba($white, 0.75);

  a {
    color: $white;
    text-decoration: underline;
  }
}

.cta-submit:disabled {
  opacity: 0.4;
  cursor: not-allowed;

  &:hover {
    background-color: $white;
  }
}

/* ── Tablet (768px) ── */

@media (min-width: 768px) {
  .final-cta-inner {
    padding: 64px 40px;
    gap: 48px;
  }

  .form-row {
    flex-direction: row;
  }

  .cta-submit {
    width: auto;
  }
}

/* ── Desktop (1440px) ── */

@media (min-width: 900px) and (orientation: landscape) {
  .final-cta-inner {
    padding: 80px 120px;
    flex-direction: row;
    align-items: flex-start;
    gap: 80px;
  }

  .final-cta-content {
    flex: 0 0 auto;
    max-width: 480px;
    position: sticky;
    top: 80px;
  }

  .final-cta-title {
    font-size: 2.4rem;
  }

  .final-cta-text {
    font-size: 1.1rem;
  }

  .final-cta-form-wrapper {
    flex: 1;
    min-width: 0;
  }
}
</style>
